import React from 'react';
import { Target, Calendar, TrendingUp, User, Award, Clock, Star, MessageSquare } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PerformanceOverview = ({ employee, performanceData, goals, department }) => {
  const getPerformanceRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-100 border-green-200';
    if (rating >= 3.5) return 'text-blue-600 bg-blue-100 border-blue-200';
    if (rating >= 2.5) return 'text-yellow-600 bg-yellow-100 border-yellow-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  const completedGoals = goals?.filter(g => g?.progress >= 100)?.length || 0;
  const inProgressGoals = goals?.filter(g => g?.progress > 0 && g?.progress < 100)?.length || 0;
  const goalCompletionRate = goals?.length > 0 ? Math.round((completedGoals / goals?.length) * 100) : 0;

  const performanceMetrics = [
    {
      label: 'Current Rating',
      value: performanceData?.currentRating || 0,
      maxValue: 5,
      format: (val) => `${val}/5`,
      color: 'blue'
    },
    {
      label: 'Goal Completion',
      value: goalCompletionRate,
      maxValue: 100,
      format: (val) => `${val}%`,
      color: 'green'
    },
    {
      label: 'Review Progress',
      value: 75,
      maxValue: 100,
      format: (val) => `${val}%`,
      color: 'purple'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Employee Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="h-8 w-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">
              {employee?.firstName} {employee?.lastName}
            </h2>
            <p className="text-gray-600">{employee?.title}</p>
            <p className="text-sm text-gray-500">{department?.name || 'No Department'}</p>
          </div>
          <div className={`px-4 py-2 rounded-full border text-center ${getPerformanceRatingColor(performanceData?.currentRating || 0)}`}>
            <div className="text-lg font-bold">{performanceData?.currentRating || '0.0'}</div>
            <div className="text-xs">Overall Rating</div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {performanceMetrics?.map((metric, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{metric?.label}</span>
              <span className="text-lg font-bold text-gray-900">{metric?.format(metric?.value)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  metric?.color === 'blue' ? 'bg-blue-600' :
                  metric?.color === 'green' ? 'bg-green-600' : 'bg-purple-600'
                }`}
                style={{ width: `${(metric?.value / metric?.maxValue) * 100}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Goals & Reviews Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="h-5 w-5 text-blue-600 mr-2" />
              Goals ({goals?.length || 0})
            </h3>
            <Button size="sm" variant="outline">Add Goal</Button>
          </div>

          {goals?.length > 0 ? (
            <div className="space-y-3">
              {/* Goal Summary */}
              <div className="grid grid-cols-3 gap-2 text-center mb-4">
                <div className="bg-green-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-green-600">{completedGoals}</div>
                  <div className="text-xs text-green-600">Completed</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-blue-600">{inProgressGoals}</div>
                  <div className="text-xs text-blue-600">In Progress</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-gray-600">{goals?.length - completedGoals - inProgressGoals}</div>
                  <div className="text-xs text-gray-600">Not Started</div>
                </div>
              </div>

              {/* Goal List */}
              <div className="space-y-3">
                {goals?.slice(0, 3)?.map((goal, index) => (
                  <div key={goal?.id || index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">{goal?.title}</h4>
                      <span className="text-xs text-gray-500">
                        {Math.round(goal?.progress || 0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full" 
                        style={{ width: `${Math.min(goal?.progress || 0, 100)}%` }}
                      ></div>
                    </div>
                    {goal?.dueDate && (
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        Due: {new Date(goal?.dueDate)?.toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
                {goals?.length > 3 && (
                  <Button variant="ghost" size="sm" className="w-full">
                    View All {goals?.length} Goals
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Target className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-4">No goals set</p>
              <Button size="sm">Create First Goal</Button>
            </div>
          )}
        </div>

        {/* Review History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 text-blue-600 mr-2" />
              Review History
            </h3>
            <Button size="sm" variant="outline">Schedule Review</Button>
          </div>

          <div className="space-y-4">
            {/* Current Review Status */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Current Review Period</span>
                <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                  {performanceData?.reviewStatus?.toUpperCase() || 'PENDING'}
                </span>
              </div>
              <p className="text-sm text-blue-800">Q4 2024 Performance Review</p>
              <div className="flex items-center mt-2 text-xs text-blue-700">
                <Clock className="h-3 w-3 mr-1" />
                Due: {performanceData?.nextReviewDate ? new Date(performanceData?.nextReviewDate)?.toLocaleDateString() : 'TBD'}
              </div>
            </div>

            {/* Previous Reviews */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Previous Reviews</h4>
              {performanceData?.lastReviewDate ? (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Q3 2024 Review</p>
                      <p className="text-xs text-gray-500">
                        {new Date(performanceData?.lastReviewDate)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm font-medium">{performanceData?.currentRating || '0.0'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Calendar className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No previous reviews</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex space-x-2">
              <Button size="sm" variant="outline" className="flex-1">
                <MessageSquare className="h-3 w-3 mr-1" />
                Add Note
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Award className="h-3 w-3 mr-1" />
                Give Feedback
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
          Performance Insights
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Strengths</h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-600">Consistently meets project deadlines</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-600">Strong collaboration with team members</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-600">Proactive in identifying solutions</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Development Areas</h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-600">Could improve presentation skills</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-600">Opportunity for leadership development</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceOverview;